import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import GlassFormField from '@/components/design-studio/GlassFormField';
import { MarketingFormPanel } from '@/components/design-studio/GlassFormScrollPanel';
import { canSubmitRequiredForm } from '@/utils/required-fields';
import {
  getPostalCodeLabel,
  getStateProvinceLabel,
  getStateProvinceOptions,
} from '@/utils/region-options';
import {
  FORM_PAGE_DESKTOP_BP,
  GLASS_FORM_FOOTER_SX,
  GLASS_FORM_ROOT_SX,
  GLASS_FORM_PANEL_PADDING_SX,
  GLASS_FORM_PAIR_STACK_SPACING,
  GLASS_FORM_ROW_SX,
  LIQUID_GLASS_FORM_SX,
  LIQUID_GLASS_PANEL_SX,
  LIQUID_GLASS_SUBMIT_BUTTON_SX,
} from '@/components/design-studio/design-studio-glass';

const ROLE_OPTIONS = [{ value: 'designer', label: 'Designer' }];

const COUNTRY_OPTIONS = [
  { value: 'Canada', label: 'Canada' },
  { value: 'USA', label: 'USA' },
];

const REQUIRED_FIELD_KEYS = [
  'first_name',
  'last_name',
  'email',
  'phone',
  'country',
  'province_state',
  'city',
  'postal_code',
  'address',
  'role',
  'consent_to_communications',
];

const INITIAL_VALUES = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  country: 'Canada',
  province_state: '',
  city: '',
  postal_code: '',
  address: '',
  role: 'designer',
  comments: '',
  consent_to_communications: false,
};

export default function DesignStudioForm({ variant = 'default' }) {
  const isGlass = variant === 'glass';
  const [values, setValues] = React.useState(INITIAL_VALUES);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const [snack, setSnack] = React.useState({
    open: false,
    message: '',
    severity: 'error',
  });

  const closeSnack = React.useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnack(prev => ({ ...prev, open: false }));
  }, []);

  const openErrorSnack = React.useCallback(message => {
    setSnack({
      open: true,
      message: message || 'Something went wrong.',
      severity: 'error',
    });
  }, []);

  const handleChange = React.useCallback(e => {
    const { name, value, type, checked } = e.target;
    setValues(prev => {
      if (name === 'country' && value !== prev.country) {
        return { ...prev, country: value, province_state: '' };
      }
      return {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
    });
  }, []);

  const stateProvinceOptions = React.useMemo(
    () => getStateProvinceOptions(values.country),
    [values.country],
  );

  const stateProvinceLabel = React.useMemo(
    () => getStateProvinceLabel(values.country),
    [values.country],
  );

  const postalCodeLabel = React.useMemo(() => getPostalCodeLabel(values.country), [values.country]);

  const canSubmit = React.useMemo(
    () => canSubmitRequiredForm(values, REQUIRED_FIELD_KEYS, ['email']),
    [values],
  );

  const handleSubmit = React.useCallback(
    async e => {
      e.preventDefault();
      setIsSubmitting(true);
      setFieldErrors({});

      try {
        const res = await fetch('/t/public/design-studio/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        });

        if (!res.ok) {
          let payload = null;
          try {
            payload = await res.json();
          } catch {
            payload = null;
          }

          if (payload && typeof payload === 'object') {
            const nextFieldErrors = {};
            Object.keys(payload).forEach(key => {
              const v = payload[key];
              if (Array.isArray(v) && v.length) nextFieldErrors[key] = String(v[0]);
              else if (typeof v === 'string') nextFieldErrors[key] = v;
            });
            setFieldErrors(nextFieldErrors);
            const detailMsg = typeof payload.detail === 'string' ? payload.detail : '';
            openErrorSnack(detailMsg || 'Please fix the highlighted fields.');
          } else {
            openErrorSnack('Submission failed. Please try again.');
          }
          return;
        }

        setSubmitted(true);
        setValues(INITIAL_VALUES);
      } catch {
        openErrorSnack('Network error. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [values, openErrorSnack],
  );

  const panelSx = isGlass
    ? { ...LIQUID_GLASS_PANEL_SX, ...GLASS_FORM_PANEL_PADDING_SX }
    : { p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider' };

  const rowSx = isGlass ? GLASS_FORM_ROW_SX : { mb: 2 };
  const rowSpacing = isGlass ? GLASS_FORM_PAIR_STACK_SPACING : { xs: 0, [FORM_PAGE_DESKTOP_BP]: 2 };

  const sectionHeadingSx = isGlass
    ? {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        mb: { xs: 1, [FORM_PAGE_DESKTOP_BP]: 0.25 },
      }
    : { mb: 2 };

  const rootSx = isGlass ? GLASS_FORM_ROOT_SX : { width: '100%' };

  return (
    <Box sx={rootSx}>
      <Stack spacing={2} sx={isGlass ? { flex: 1, minHeight: 0 } : undefined}>
        {submitted && (
          <Alert
            severity="success"
            sx={
              isGlass
                ? {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'common.white',
                    border: '1px solid rgba(255, 255, 255, 0.35)',
                    backdropFilter: 'blur(12px)',
                    '& .MuiAlert-icon': { color: 'common.white' },
                  }
                : undefined
            }
          >
            Thank you for your application. Our team will be in touch soon.
          </Alert>
        )}

        {!submitted && (
          <MarketingFormPanel
            isGlass={isGlass}
            panelSx={panelSx}
            formSx={isGlass ? LIQUID_GLASS_FORM_SX : undefined}
            onSubmit={handleSubmit}
            header={
              <Box sx={rowSx}>
                <Typography
                  component="h2"
                  variant={isGlass ? undefined : 'h6'}
                  fontWeight={600}
                  sx={sectionHeadingSx}
                >
                  Designer Contact Information
                </Typography>
              </Box>
            }
            footer={
              <Box sx={isGlass ? GLASS_FORM_FOOTER_SX : { mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="consent_to_communications"
                        checked={values.consent_to_communications}
                        onChange={handleChange}
                        color="primary"
                        size={isGlass ? 'small' : 'medium'}
                      />
                    }
                    label="I consent to receive future communications from NIICO Millwork."
                    sx={{ m: 0, alignItems: 'flex-start' }}
                  />
                  {fieldErrors.consent_to_communications && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {fieldErrors.consent_to_communications}
                    </Typography>
                  )}
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  size={isGlass ? 'medium' : 'large'}
                  disableElevation
                  disabled={isSubmitting || !canSubmit}
                  sx={
                    isGlass
                      ? {
                          ...LIQUID_GLASS_SUBMIT_BUTTON_SX,
                          py: 1,
                          minWidth: 120,
                          alignSelf: { xs: 'flex-end', [FORM_PAGE_DESKTOP_BP]: 'center' },
                        }
                      : { borderRadius: 2, py: 1.25, minWidth: 140, alignSelf: 'flex-end' }
                  }
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            }
          >
            <Stack
              direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }}
              spacing={rowSpacing}
              sx={rowSx}
            >
              <GlassFormField
                isGlass={isGlass}
                name="first_name"
                label="First Name"
                value={values.first_name}
                onChange={handleChange}
                fullWidth
                required
                error={Boolean(fieldErrors.first_name)}
                helperText={fieldErrors.first_name}
                autoComplete="given-name"
              />
              <GlassFormField
                isGlass={isGlass}
                name="last_name"
                label="Last Name"
                value={values.last_name}
                onChange={handleChange}
                fullWidth
                required
                error={Boolean(fieldErrors.last_name)}
                helperText={fieldErrors.last_name}
                autoComplete="family-name"
              />
            </Stack>

            <Stack
              direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }}
              spacing={rowSpacing}
              sx={rowSx}
            >
              <GlassFormField
                isGlass={isGlass}
                name="email"
                label="Email"
                type="email"
                value={values.email}
                onChange={handleChange}
                fullWidth
                required
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
                autoComplete="email"
              />
              <GlassFormField
                isGlass={isGlass}
                name="phone"
                label="Phone"
                value={values.phone}
                onChange={handleChange}
                fullWidth
                required
                error={Boolean(fieldErrors.phone)}
                helperText={fieldErrors.phone}
                autoComplete="tel"
              />
            </Stack>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="address"
                label="Address"
                value={values.address}
                onChange={handleChange}
                fullWidth
                required
                error={Boolean(fieldErrors.address)}
                helperText={fieldErrors.address}
                autoComplete="street-address"
              />
            </Box>

            <Stack
              direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }}
              spacing={rowSpacing}
              sx={rowSx}
            >
              <GlassFormField
                isGlass={isGlass}
                name="country"
                label="Country"
                value={values.country}
                onChange={handleChange}
                fullWidth
                select
                required
                error={Boolean(fieldErrors.country)}
                helperText={fieldErrors.country}
              >
                {COUNTRY_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </GlassFormField>
              <GlassFormField
                isGlass={isGlass}
                name="province_state"
                label={stateProvinceLabel}
                value={values.province_state}
                onChange={handleChange}
                fullWidth
                select
                required
                error={Boolean(fieldErrors.province_state)}
                helperText={fieldErrors.province_state}
              >
                <MenuItem value="" disabled>
                  Select one
                </MenuItem>
                {stateProvinceOptions.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </GlassFormField>
            </Stack>

            <Stack
              direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }}
              spacing={rowSpacing}
              sx={rowSx}
            >
              <GlassFormField
                isGlass={isGlass}
                name="city"
                label="City"
                value={values.city}
                onChange={handleChange}
                fullWidth
                required
                error={Boolean(fieldErrors.city)}
                helperText={fieldErrors.city}
                autoComplete="address-level2"
              />
              <GlassFormField
                isGlass={isGlass}
                name="postal_code"
                label={postalCodeLabel}
                value={values.postal_code}
                onChange={handleChange}
                fullWidth
                required
                error={Boolean(fieldErrors.postal_code)}
                helperText={fieldErrors.postal_code}
                autoComplete="postal-code"
              />
            </Stack>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="role"
                label="I am a..."
                value={values.role}
                onChange={handleChange}
                fullWidth
                select
                required
                error={Boolean(fieldErrors.role)}
                helperText={fieldErrors.role}
              >
                {ROLE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </GlassFormField>
            </Box>

            <Box sx={rowSx}>
              <GlassFormField
                isGlass={isGlass}
                name="comments"
                label="Comments"
                value={values.comments}
                onChange={handleChange}
                fullWidth
                multiline
                maxRows={4}
                error={Boolean(fieldErrors.comments)}
                helperText={fieldErrors.comments}
              />
            </Box>
          </MarketingFormPanel>
        )}

        <Snackbar open={snack.open} autoHideDuration={6000} onClose={closeSnack}>
          <Alert
            onClose={closeSnack}
            severity={snack.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Stack>
    </Box>
  );
}

DesignStudioForm.propTypes = {
  variant: PropTypes.oneOf(['default', 'glass']),
};
